import { useEffect, useMemo } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { DEFAULT_FACTURATION_CONFIG, normalizeFacturationConfig } from './facturationConfigDefaults';

let hydratePromise = null;

function hydrateFacturationConfig(patchSettings, existingSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/facturation/config')
      .then((response) => {
        const raw = response?.data !== undefined ? response.data : response;
        const normalized = normalizeFacturationConfig(raw || {});
        syncFacturationConfigToSettings(normalized, patchSettings, existingSettings);
        return normalized;
      })
      .catch(() => {
        hydratePromise = null;
        return DEFAULT_FACTURATION_CONFIG;
      });
  }
  return hydratePromise;
}

/** Bridge module config into legacy settings paths used by DocumentCreation & archives. */
export function syncFacturationConfigToSettings(config, patchSettings, existingSettings) {
  const normalized = normalizeFacturationConfig(config);
  const {
    documentPrefixes,
    entityPrefixes,
    documentDelays,
    deliveryTimeframes,
    clientDiscounts,
    defaultTaxRate,
    defaultPaymentTermsDays,
    defaultPaymentMethod,
    vatMode,
    showTva,
    tableType,
    showReferenceColumn,
  } = normalized;

  const existingConfig = existingSettings?.configuration || {};

  const patch = {
    facturation: normalized,
    configuration: {
      ...(documentPrefixes && {
        documentPrefixes: {
          ...(existingConfig.documentPrefixes || {}),
          ...documentPrefixes,
        },
      }),
      ...(entityPrefixes && {
        entityPrefixes: {
          ...(existingConfig.entityPrefixes || {}),
          ...entityPrefixes,
        },
      }),
      ...(documentDelays && {
        documentDelays: {
          ...(existingConfig.documentDelays || {}),
          ...documentDelays,
        },
      }),
      ...(deliveryTimeframes && {
        deliveryTimeframes: {
          ...(existingConfig.deliveryTimeframes || {}),
          ...deliveryTimeframes,
        },
      }),
      ...(clientDiscounts && {
        clientDiscounts: {
          ...(existingConfig.clientDiscounts || {}),
          ...clientDiscounts,
        },
      }),
      ...(defaultPaymentTermsDays != null && { defaultPaymentTermsDays }),
    },
    invoicing: {
      ...(tableType && { tableType }),
      ...(vatMode && { vatMode }),
      showTva: showTva !== false,
      showReferenceColumn: showReferenceColumn !== false,
      ...(defaultPaymentTermsDays != null && { defaultPaymentTermsDays }),
      ...(defaultPaymentMethod && { defaultPaymentMethod }),
    },
  };

  if (defaultTaxRate != null) {
    patch.taxes = { tva: defaultTaxRate };
  }

  patchSettings(patch, { persist: false });
  return normalized;
}

/** Live facturation module config — synced from API once, then from SettingsContext. */
export function useFacturationConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (settings?.facturation && Object.keys(settings.facturation).length > 0) return;
    void hydrateFacturationConfig(patchSettings, settings);
  }, [settings?.facturation, settings, patchSettings]);

  return useMemo(
    () => normalizeFacturationConfig(settings?.facturation || {}),
    [settings?.facturation],
  );
}
